<template>
  <div class="custom-sql-container">
    <!-- SQL Query Input Section -->
    <div class="sql-input-section">
      <div class="input-header">
        <h5>Custom SQL Query</h5>
        <div class="query-actions">
          <button 
            @click="clearQuery" 
            class="clear-button"
            :disabled="!sqlQuery.trim()">
            <i class="fas fa-eraser"></i>
            Clear
          </button>
          <button 
            @click="executeQuery" 
            class="execute-button"
            :disabled="isLoading || !sqlQuery.trim()">
            <i class="fas fa-play" v-if="!isLoading"></i>
            <i class="fas fa-spinner fa-spin" v-else></i>
            {{ isLoading ? 'Executing...' : 'Execute Query' }}
          </button>
        </div>
      </div>
      
      <div class="queries-row">
        <!-- Query History Section -->
        <div class="queries-section history-section">
          <div class="section-header">
            <h5><i class="fas fa-history"></i> Query History</h5>
            <button @click="clearHistory" class="clear-history-btn" title="Clear History" :disabled="queryHistory.length === 0">
              <i class="fas fa-trash"></i>
            </button>
          </div>
          <div class="queries-scroll-container">
            <div v-if="queryHistory.length === 0" class="empty-history">
              <i class="fas fa-inbox"></i>
              <p>No query history yet</p>
            </div>
            <div 
              v-for="(historyItem, index) in queryHistory" 
              :key="index"
              @click="useHistoryQuery(historyItem.query)"
              class="query-chip history-query-chip">
              <div class="query-chip-preview">{{ historyItem.query }}</div>
              <div class="query-chip-timestamp">{{ formatTimestamp(historyItem.timestamp) }}</div>
            </div>
          </div>
        </div>
         <!-- Sample Queries Section -->
        <div class="queries-section sample-queries-section">
          <div class="section-header">
            <h5><i class="fas fa-lightbulb"></i> Sample Queries</h5>
          </div>
          <div class="queries-scroll-container">
            <div 
              v-for="(sample, index) in dynamicSampleQueries" 
              :key="index"
              @click="useSampleQuery(sample.query)"
              class="query-chip sample-query-chip">
              <div class="query-chip-name">{{ sample.name }}</div>
              <div class="query-chip-preview">{{ sample.query }}</div>
            </div>
          </div>
        </div>
      </div>
      
      <div class="query-input-container">
        <textarea
          v-model="sqlQuery"
          placeholder="Enter your SQL query here...&#10;Example: SELECT * FROM price_actuals WHERE plant_id = 1 AND date_added > '2025-07-09 07:00:00' AND date_added < '2025-07-09 10:00:00'"
          class="sql-textarea"
          :disabled="isLoading"
          rows="6"
        ></textarea>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="loading-container">
      <div class="loading-spinner"></div>
      <p>Executing SQL query...</p>
    </div>

    <!-- Results Section -->
    <div v-if="!isLoading && queryResults.length > 0" class="results-section">
      <div class="results-header">
        <h5>Query Results ({{ queryResults.length }} rows)</h5>
        <button @click="exportToCSV" class="export-button">
          <i class="fas fa-file-csv"></i>
          Export to CSV
        </button>
      </div>
      
      <div class="table-container">
        <table class="results-table">
          <thead>
            <tr>
              <th v-for="column in tableColumns" :key="column" class="column-header">
                {{ column }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, index) in queryResults" :key="index">
              <td v-for="column in tableColumns" :key="column" class="cell-data">
                {{ formatCellValue(row[column]) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Empty State -->
    <div v-if="!isLoading && queryResults.length === 0 && hasExecuted" class="empty-state">
      <div class="empty-icon">
        <i class="fas fa-database"></i>
      </div>
      <h5>No results found</h5>
      <p>Your query executed successfully but returned no data.</p>
    </div>

    <!-- Error State -->
    <div v-if="error" class="error-container">
      <div class="error-icon">
        <i class="fas fa-exclamation-triangle"></i>
      </div>
      <h5>Query Error</h5>
      <p>{{ error }}</p>
      <button @click="executeQuery" class="retry-button">
        <i class="fas fa-redo"></i>
        Try Again
      </button>
    </div>
  </div>
</template>

<script>
import { apiRequest } from '@/services/apiService';
import { getDevModeState } from '@/services/cookieService';

export default {
  name: 'CustomSqlQuery',
  data() {
    return {
      sqlQuery: '',
      isLoading: false,
      queryResults: [],
      tableColumns: [],
      error: null,
      hasExecuted: false,
      subDataUrl: '/api/v1.1/data',
              sampleQueries: [
          {
            name: 'Price Actuals',
            query: "SELECT * FROM price_actuals WHERE plant_id = 1 AND date_added > '2025-07-09 07:00:00' AND date_added < '2025-07-09 10:00:00'"
          },
          {
            name: 'Action Reasons',
            query: "SELECT * FROM action_reasons WHERE plant_id = 27 AND date_added > '2025-07-16 14:30:00' AND date_added < '2025-07-16 14:59:59'"
          },
          {
            name: 'Price Actuals Today',
            query: "SELECT * FROM price_actuals WHERE plant_id = 27 AND date_added > 'TODAY_START' AND date_added < 'TODAY_END'"
          },
          {
            name: 'Action Reasons Today',
            query: "SELECT * FROM action_reasons WHERE plant_id = 27 AND date_added > 'TODAY_START' AND date_added < 'TODAY_END'"
          },      
        ],
      queryHistory: []
    };
  },
  methods: {
    readFromCookies() {
      // Initialize subDataUrl based on dev mode state from cookies
      const devModeState = getDevModeState();
      if (devModeState.isDevMode !== undefined) {
        this.subDataUrl = devModeState.isDevMode ? "/dev-api/api/v1.1/data" : "/api/v1.1/data";
      }
    },

    async executeQuery() {
      if (!this.sqlQuery.trim()) {
        this.error = 'Please enter a SQL query';
        return;
      }

      this.isLoading = true;
      this.error = null;
      this.hasExecuted = true;

      // Add query to history
      this.addToHistory(this.sqlQuery.trim());

      try {
        const bodyData = {
          query: this.sqlQuery.trim()
        };
        
        const url = `${this.subDataUrl}/sql/query/get`;
        const response = await apiRequest('post', url, bodyData);
        
        // console.log('SQL Query Response:', response);
        // console.log('Response data:', response.data);
        // console.log('Response data.data:', response.data?.data);
        
        // Handle different possible response structures
        let dataToProcess = null;
        
        if (response.data && response.data.data && response.data.data.columns && response.data.data.rows) {
          dataToProcess = response.data.data;
        } else if (response.data && response.data.columns && response.data.rows) {
          dataToProcess = response.data;
        }
        
        if (dataToProcess) {
          const { columns, rows } = dataToProcess;
          this.tableColumns = columns;
          
          // Convert rows array to array of objects with column names as keys
          this.queryResults = rows.map(row => {
            const rowObj = {};
            columns.forEach((column, index) => {
              rowObj[column] = row[index];
            });
            return rowObj;
          });
          
          // console.log('Table columns:', this.tableColumns);
          // console.log('Query results:', this.queryResults);
          // console.log('Results length:', this.queryResults.length);
        } else {
          this.queryResults = [];
          this.tableColumns = [];
          // console.log('No valid data structure found in response');
        }
      } catch (error) {
        // console.error('Error executing SQL query:', error);
        this.error = 'Failed to execute query. Please check your SQL syntax and try again.';
        this.queryResults = [];
        this.tableColumns = [];
      } finally {
        this.isLoading = false;
      }
    },

    clearQuery() {
      this.sqlQuery = '';
      this.queryResults = [];
      this.tableColumns = [];
      this.error = null;
      this.hasExecuted = false;
    },



    useSampleQuery(query) {
      this.sqlQuery = query;
    },

    useHistoryQuery(query) {
      this.sqlQuery = query;
    },

    addToHistory(query) {
      // Remove if already exists to avoid duplicates
      this.queryHistory = this.queryHistory.filter(item => item.query !== query);
      
      // Add to beginning of array
      this.queryHistory.unshift({
        query: query,
        timestamp: new Date()
      });
      
      // Keep only last 20 queries
      if (this.queryHistory.length > 20) {
        this.queryHistory = this.queryHistory.slice(0, 20);
      }
      
      // Save to localStorage
      this.saveHistoryToStorage();
    },

    clearHistory() {
      this.queryHistory = [];
      this.saveHistoryToStorage();
    },

    saveHistoryToStorage() {
      try {
        localStorage.setItem('sqlQueryHistory', JSON.stringify(this.queryHistory));
      } catch (error) {
        console.warn('Failed to save query history to localStorage:', error);
      }
    },

    loadHistoryFromStorage() {
      try {
        const saved = localStorage.getItem('sqlQueryHistory');
        if (saved) {
          this.queryHistory = JSON.parse(saved);
        }
      } catch (error) {
        console.warn('Failed to load query history from localStorage:', error);
        this.queryHistory = [];
      }
    },

    formatTimestamp(timestamp) {
      const date = new Date(timestamp);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;
      
      return date.toLocaleDateString();
    },
    formatCellValue(value) {
      if (value === null || value === undefined) return '-';
      
      if (typeof value === 'string') {
        // More robust date detection
        if (this.isValidDateString(value)) {
          try {
            const date = new Date(value);
            // Additional check to ensure the date is valid
            if (!isNaN(date.getTime())) {
              return date.toLocaleString();
            }
          } catch {
            // Fall through to return original value
          }
        }
      }
      
      return String(value);
    },

    isValidDateString(str) {
      // Check for common date formats
      const datePatterns = [
        // ISO 8601 formats
        /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?([+-]\d{2}:?\d{2}|Z)$/,
        // RFC 2822 format
        /^[A-Za-z]{3},?\s\d{1,2}\s[A-Za-z]{3}\s\d{4}\s\d{2}:\d{2}:\d{2}\s[+-]\d{4}$/,
        // Other common formats with timezone
        /^\d{4}-\d{2}-\d{2}\s\d{2}:\d{2}:\d{2}[+-]\d{2}:?\d{2}$/,
      ];
      
      // First check if it matches common date patterns
      const matchesPattern = datePatterns.some(pattern => pattern.test(str));
      if (!matchesPattern) return false;
      
      // Additional validation: try to parse and check if it's a reasonable date
      const date = new Date(str);
      if (isNaN(date.getTime())) return false;
      
      // Check if the date is within a reasonable range (e.g., between 1900 and 2100)
      const year = date.getFullYear();
      if (year < 1900 || year > 2100) return false;
      
      return true;
    },

    // Alternative simpler approach using built-in validation
    formatCellValueSimple(value) {
      if (value === null || value === undefined) return '-';
      
      if (typeof value === 'string') {
        // Only attempt date parsing if the string looks like a timestamp
        if (value.length >= 10 && // Minimum length for a date
            (value.includes('T') || value.includes(' ')) && // Has date-time separator
            (value.includes('+') || value.includes('-') || value.includes('Z'))) { // Has timezone indicator
          
          try {
            const date = new Date(value);
            // Check if it's a valid date and not NaN
            if (!isNaN(date.getTime())) {
              // Additional sanity check - ensure it's a reasonable date
              const year = date.getFullYear();
              if (year >= 1900 && year <= 2100) {
                return date.toLocaleString();
              }
            }
          } catch {
            // Fall through to return original value
          }
        }
      }
      
      return String(value);
    },
    exportToCSV() {
      if (this.queryResults.length === 0) {
        this.error = 'No data to export';
        return;
      }

      try {
        // Create CSV content
        const csvContent = this.convertToCSV(this.queryResults);
        
        // Create and download file
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        
        link.setAttribute('href', url);
        link.setAttribute('download', `sql_query_results_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        // console.error('Error exporting CSV:', error);
        this.error = 'Failed to export CSV file';
      }
    },

    convertToCSV(data) {
      if (data.length === 0) return '';
      
      // Get headers from the first object
      const headers = Object.keys(data[0]);
      
      // Create CSV header row
      const csvRows = [headers.join(',')];
      
      // Add data rows
      for (const row of data) {
        const values = headers.map(header => {
          const value = row[header];
          // Handle values that contain commas, quotes, or newlines
          if (value === null || value === undefined) return '';
          const stringValue = String(value);
          if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
            return `"${stringValue.replace(/"/g, '""')}"`;
          }
          return stringValue;
        });
        csvRows.push(values.join(','));
      }
      
      return csvRows.join('\n');
    }
  },

  computed: {
    dynamicSampleQueries() {
      const today = new Date();
      const todayStart = today.toISOString().split('T')[0] + ' 00:00:00';
      const todayEnd = today.toISOString().split('T')[0] + ' 23:59:59';
      
      return this.sampleQueries.map(sample => ({
        ...sample,
        query: sample.query
          .replace(/TODAY_START/g, todayStart)
          .replace(/TODAY_END/g, todayEnd)
      }));
    }
  },

  mounted() {
    // Initialize subDataUrl from cookies
    this.readFromCookies();
    // Load query history from localStorage
    this.loadHistoryFromStorage();
  }
};
</script>

<style scoped>
.custom-sql-container {
  width: 100%;
}

/* SQL Input Section */
.sql-input-section {
  background: var(--card-bg);
  border-radius: 12px;
  box-shadow: 0 2px 10px var(--shadow-color);
  margin-bottom: 30px;
  overflow: hidden;
}

.input-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 25px;
  border-bottom: 1px solid var(--border-color);
  background: var(--bg-secondary);
}

.input-header h5 {
  margin: 0;
  color: var(--text-primary);
}

.query-actions {
  display: flex;
  gap: 10px;
}



.clear-button {
  background: var(--text-tertiary);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.9rem;
}

.clear-button:hover:not(:disabled) {
  background: #5a6268;
  transform: translateY(-1px);
}

.clear-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.execute-button {
  background: linear-gradient(135deg, #27ae60, #2ecc71);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.9rem;
}

.execute-button:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(39, 174, 96, 0.3);
}

.execute-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.query-input-container {
  padding: 20px 25px;
}

.sql-textarea {
  width: 100%;
  min-height: 120px;
  padding: 15px;
  border: 2px solid var(--border-color);
  border-radius: 8px;
  font-family: 'Courier New', monospace;
  font-size: 0.9rem;
  line-height: 1.4;
  resize: vertical;
  transition: border-color 0.3s ease;
  box-sizing: border-box;
  background: var(--input-bg);
  color: var(--text-primary);
}

.sql-textarea:focus {
  outline: none;
  border-color: #3498db;
}

.sql-textarea:disabled {
  background: var(--bg-tertiary);
  cursor: not-allowed;
}

/* Queries Sections */
.queries-row {
  display: flex;
  gap: 24px;
  width: 100%;
}
.queries-section {
  flex: 1 1 0;
  min-width: 0;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 25px;
  border-bottom: 1px solid var(--border-color);
}

.section-header h4,
.section-header h5 {
  margin: 0;
  color: var(--text-primary);
  font-size: 0.95rem;
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
}

.clear-history-btn {
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.2s ease;
  font-size: 0.8rem;
}

.clear-history-btn:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}

.clear-history-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.clear-history-btn:disabled:hover {
  background: none;
  color: var(--text-secondary);
}

.queries-scroll-container {
  padding: 15px 25px;
  max-height: 120px;
  overflow-y: auto;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.query-chip {
  background: var(--card-bg);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 10px 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 200px;
  max-width: 300px;
  flex-shrink: 0;
}

.query-chip:hover {
  border-color: #3498db;
  box-shadow: 0 2px 8px rgba(52, 152, 219, 0.15);
  transform: translateY(-1px);
}

.sample-query-chip .query-chip-name {
  font-weight: 400;      /* Not bold */
  color: var(--text-primary);
  margin-bottom: 4px;
  font-size: 0.75rem;    /* Smaller */
}

.query-chip-preview {
  color: var(--text-secondary);
  font-family: 'Courier New', monospace;
  font-size: 0.75rem;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.history-query-chip .query-chip-timestamp {
  font-size: 0.7rem;
  color: var(--text-tertiary);
  margin-top: 4px;
}

.empty-history {
  text-align: center;
  padding: 20px;
  color: var(--text-tertiary);
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.empty-history i {
  font-size: 1.5rem;
  opacity: 0.5;
}

.empty-history p {
  margin: 0;
  font-size: 0.85rem;
}

/* Loading State */
.loading-container {
  text-align: center;
  padding: 40px;
  background: var(--card-bg);
  border-radius: 12px;
  box-shadow: 0 2px 10px var(--shadow-color);
  margin-bottom: 30px;
  border: 1px solid var(--border-color);
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid var(--bg-tertiary);
  border-top: 4px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Results Section */
.results-section {
  background: var(--card-bg);
  border-radius: 12px;
  box-shadow: 0 2px 10px var(--shadow-color);
  overflow: hidden;
  width: 100%;
  border: 1px solid var(--border-color);
}

.results-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 25px;
  border-bottom: 1px solid var(--border-color);
  background: var(--bg-secondary);
}

.results-header h5 {
  margin: 0;
  color: var(--text-primary);
}

.export-button {
  background: linear-gradient(135deg, #e67e22, #f39c12);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
}

.export-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(230, 126, 34, 0.3);
}

/* Table Styles */
.table-container {
  overflow-x: auto;
  max-height: 600px;
  overflow-y: auto;
  width: 100%;
}

.results-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
  table-layout: auto;
  min-width: 800px;
}

.results-table th {
  background: #34495e;
  color: white;
  padding: 12px 8px;
  text-align: left;
  font-weight: 600;
  position: sticky;
  top: 0;
  z-index: 10;
  white-space: normal;
  word-wrap: break-word;
  overflow-wrap: break-word;
  max-width: 200px;
}

.results-table td {
  padding: 10px 8px;
  border-bottom: 1px solid var(--border-color);
  color: var(--text-primary);
  white-space: normal;
  word-wrap: break-word;
  overflow-wrap: break-word;
  vertical-align: top;
  max-width: 200px;
}

.results-table tbody tr:nth-child(even) {
  background: var(--bg-secondary);
}

.results-table tbody tr:hover {
  background: var(--bg-tertiary);
}

.column-header {
  min-width: 100px;
  max-width: 200px;
}

.cell-data {
  min-width: 100px;
  max-width: 200px;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 60px 20px;
  background: var(--card-bg);
  border-radius: 12px;
  box-shadow: 0 2px 10px var(--shadow-color);
  margin-bottom: 30px;
  border: 1px solid var(--border-color);
}

.empty-icon {
  font-size: 3rem;
  color: var(--text-tertiary);
  margin-bottom: 20px;
}

.empty-state h5 {
  color: var(--text-primary);
  margin-bottom: 10px;
}

.empty-state p {
  color: var(--text-secondary);
  margin: 0;
}

/* Error State */
.error-container {
  text-align: center;
  padding: 40px 20px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  margin-bottom: 30px;
}

.error-icon {
  font-size: 2.5rem;
  color: #e53e3e;
  margin-bottom: 15px;
}

.error-container h5 {
  color: var(--text-primary);
  margin-bottom: 10px;
}

.error-container p {
  color: var(--text-secondary);
  margin-bottom: 20px;
}

.retry-button {
  background: #e53e3e;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.retry-button:hover {
  background: #c53030;
  transform: translateY(-1px);
}

/* Responsive Design */
@media (max-width: 900px) {
  .queries-row {
    flex-direction: column;
    gap: 0;
  }
}

@media (max-width: 768px) {
  .input-header {
    flex-direction: column;
    gap: 15px;
    align-items: stretch;
  }
  
  .query-actions {
    justify-content: center;
    flex-wrap: wrap;
  }
  
  .results-header {
    flex-direction: column;
    gap: 15px;
    align-items: stretch;
  }
  
  .results-table {
    font-size: 0.75rem;
    min-width: 600px;
  }
  
  .results-table th,
  .results-table td {
    padding: 6px 4px;
  }

  .queries-scroll-container {
    max-height: 100px;
    padding: 10px 15px;
  }
  
  .query-chip {
    min-width: 150px;
    max-width: 200px;
    padding: 8px 10px;
  }
}

@media (max-width: 480px) {
  .results-table {
    font-size: 0.7rem;
    min-width: 500px;
  }
  
  .results-table th,
  .results-table td {
    padding: 4px 2px;
  }
}
</style> 